import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart3, Loader2 } from "lucide-react";

/**
 * AmbassadorAttributionCard — per-landing-page hit counts for the
 * currently-authenticated ambassador.
 *
 * Answers "which of my share links is actually working?" — the top
 * bar shows the raw hit-to-signup funnel headline, then a stacked
 * list of paths sorted by hits so the ambassador can double down on
 * whichever niche URL is pulling.
 */
const HUMAN_LABEL = {
  "/": "Home",
  "/for/clients": "Seekers / Clients",
  "/elders": "Founding Elders",
  "/ambassadors": "Ambassador Program",
};

function humanise(path) {
  if (HUMAN_LABEL[path]) return HUMAN_LABEL[path];
  if (path.startsWith("/for/")) {
    const slug = path.slice(5);
    return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return path;
}

export default function AmbassadorAttributionCard({ days = 30 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/ambassador/my-attribution?days=${days}`);
        if (!cancelled) setData(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (!data) {
    return (
      <div className="parchment p-5" data-testid="attribution-loading">
        <div className="text-sm text-foreground/60"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading attribution…</div>
      </div>
    );
  }

  const maxHits = data.hits.reduce((m, r) => Math.max(m, r.hits), 0) || 1;
  const conversionPct = data.totalHits > 0
    ? ((data.totalSignups / data.totalHits) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="parchment p-6" data-testid="attribution-card">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-secondary" />
          <div className="label-mystic text-secondary">Landing-page Attribution</div>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-foreground/50">
          Last {data.days} days
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="text-center border-r border-secondary/15 last:border-0">
          <div className="font-serif text-2xl text-secondary">{data.totalHits}</div>
          <div className="label-mystic text-[10px] text-foreground/60 mt-1">Link Hits</div>
        </div>
        <div className="text-center border-r border-secondary/15">
          <div className="font-serif text-2xl text-primary">{data.totalSignups}</div>
          <div className="label-mystic text-[10px] text-foreground/60 mt-1">Signups</div>
        </div>
        <div className="text-center">
          <div className="font-serif text-2xl text-foreground/85">{conversionPct}%</div>
          <div className="label-mystic text-[10px] text-foreground/60 mt-1">Conversion</div>
        </div>
      </div>

      {data.hits.length === 0 ? (
        <div className="text-sm text-foreground/55 italic">
          No hits recorded yet — start sharing your landing links from the section above.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="attribution-rows">
          {data.hits.map((r, i) => {
            const pct = Math.max(6, (r.hits / maxHits) * 100);
            return (
              <div key={i} className="flex items-center gap-3" data-testid={`attribution-row-${i}`}>
                <div className="w-48 truncate text-sm">{humanise(r.path)}</div>
                <div className="flex-1 h-4 bg-foreground/5 rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-secondary/60"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-10 text-right text-sm font-serif text-secondary">{r.hits}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
